// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { debounce } from 'lodash/fp';

// Utils
import delay from '../../../../../../../../common/utils/lib/delay';
import {
    getIsPublishedPasswordProtected,
    getTitle,
} from '../../../../../../../../common/elements/utils/elementPropertyUtils';

// Services
import { removePublishedBoardPassword, updatePublishedBoardPassword } from '../publishBoardActions';

// Hooks
import useFocusOnTrue from '../../../../../../../utils/react/useFocusOnTrue';

// Components
import Icon from '../../../../../../../components/icons/Icon';
import ToggleInput from '../../../../../../../components/form/fields/ToggleInput';
import TooltipSource from '../../../../../../../components/tooltips/TooltipSource';
import SettingsRow from '../../../../../../../user/account/accountModal/components/SettingsRow';
import StyledPopupInputContainer from '../../../../../../../components/form/fields/StyledPopupInputContainer';
import ToggleContents from '../../../../../../../components/form/fields/ToggleContents';

// Constants
import { TIMES } from '../../../../../../../../common/utils/timeUtil';
import { TooltipPositions } from '../../../../../../../components/tooltips/tooltipConstants';
import { TOGGLE_BUTTON_TRANSITION_LENGTH } from '../../../../../../../components/form/fields/ToggleButton';

// Styles
import './PublishBoardPasswordToggle.scss';

// 50 chars for a password seems like enough.
const MAX_CHARS = 50;

const mapDispatchToProps = (dispatch) => ({
    dispatchUpdatePublishedBoardPassword: (args) => dispatch(updatePublishedBoardPassword(args)),
    dispatchRemovePublishedBoardPassword: (args) => dispatch(removePublishedBoardPassword(args)),
});

const PublishBoardPasswordToggle = (props) => {
    const {
        passwordEnabled,
        setPasswordEnabled,
        isPublished,
        canModifySettings,
        currentBoard,
        currentBoardId,
        currentBoardPublishedPermissionId,
        showSharedAncestorBoardLink,
        publishedAncestorBoard,
        isPublishedAncestorPasswordProtected,
        dispatchUpdatePublishedBoardPassword,
        dispatchRemovePublishedBoardPassword,
    } = props;

    const [password, setPassword] = React.useState(getIsPublishedPasswordProtected(currentBoard) ? '••••••••••••' : '');
    const [usingPersistedPassword, setIsUsingPersistedPassword] = React.useState(
        getIsPublishedPasswordProtected(currentBoard),
    );
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    // Need to use refs because of the debounced function
    const passwordEnabledRef = React.useRef();
    passwordEnabledRef.current = passwordEnabled;
    const passwordRef = React.useRef();
    passwordRef.current = password;

    const passwordInputRef = React.useRef();

    let debouncedPersist;

    // The user can only edit the password if it's the published board, or it's not currently published
    const readOnly = usingPersistedPassword;

    // Focus the password input when it's toggled on
    useFocusOnTrue(passwordInputRef, passwordEnabled && !readOnly, TOGGLE_BUTTON_TRANSITION_LENGTH + 40);

    const updatePassword = async (newPassword) => {
        // If there's an error we should return it to its previous state (if necessary)
        const wasEnabled = passwordEnabledRef.current;

        setIsLoading(true);
        setPasswordEnabled(true);

        try {
            await dispatchUpdatePublishedBoardPassword({
                boardId: currentBoardId,
                permissionId: currentBoardPublishedPermissionId,
                password: newPassword,
            });
        } catch (err) {
            setError(err);
            await delay(1500);
            setPasswordEnabled(wasEnabled);
            setError(null);
        }

        setIsLoading(false);
    };

    const removePassword = async () => {
        // If there's a queued persistence, ignore it
        if (debouncedPersist?.cancel) debouncedPersist.cancel();

        setIsLoading(true);
        setPasswordEnabled(false);

        try {
            await dispatchRemovePublishedBoardPassword({
                boardId: currentBoardId,
                permissionId: currentBoardPublishedPermissionId,
            });
        } catch (err) {
            setError(err);
            await delay(1500);
            setPasswordEnabled(true);
            setError(null);
        }

        setIsLoading(false);
    };

    const onToggle = async (event) => {
        const nowEnabled = event.target.checked;

        if (!nowEnabled) return removePassword();

        // If there's a persisted password and we're re-enabling the password, then just turn it on
        const shouldPersist =
            nowEnabled && (usingPersistedPassword || passwordRef.current) && currentBoardPublishedPermissionId;
        if (shouldPersist) return updatePassword();

        setPasswordEnabled(true);
    };

    const clearExistingPassword = () => {
        setPassword('');
        setIsUsingPersistedPassword(false);
    };

    const persistPassword = () => {
        const newPassword = passwordRef.current;

        // Don't attempt to save the password if there's no password or if it hasn't changed
        if (!newPassword || newPassword === password) return;

        updatePassword(passwordRef.current);
    };

    debouncedPersist = React.useCallback(debounce(1000, persistPassword), [currentBoardPublishedPermissionId]);

    // Ensures the password is no longer than 50 characters
    const onPasswordChange = (event) => {
        const text = event.target.value || '';
        const updateValue = text.substr(0, MAX_CHARS);
        setPassword(updateValue);
        debouncedPersist();
    };

    const onBlur = () => {
        debouncedPersist?.flush && debouncedPersist.flush();
    };

    return (
        <div className="PublishBoardPasswordToggle">
            <SettingsRow
                className="secondary-row"
                disabled={!isPublished}
                loading={isLoading}
                label="Require a password"
            >
                <TooltipSource
                    className="share-link-toggle-tooltip small-text"
                    enabled={!!showSharedAncestorBoardLink}
                    triggerOnClick
                    position={TooltipPositions.BOTTOM}
                    duration={TIMES.SECOND * 3}
                    tooltipText={`Change this setting in: ${getTitle(publishedAncestorBoard)}`}
                >
                    <ToggleInput
                        disabled={!canModifySettings}
                        error={!!error}
                        // Show the password as enabled if a published ancestor requires a password
                        active={passwordEnabled || isPublishedAncestorPasswordProtected}
                        onChange={onToggle}
                    />
                </TooltipSource>
            </SettingsRow>
            <ToggleContents
                // Only show the password input field if it's the current board that's published
                active={passwordEnabled}
                disabled={!canModifySettings}
                loading={isLoading}
            >
                <div className="setting-contents">
                    <StyledPopupInputContainer readOnly={readOnly} inputRef={passwordInputRef}>
                        <Icon name="lock" />
                        <input
                            ref={passwordInputRef}
                            className="password-input"
                            disabled={readOnly}
                            type="text"
                            placeholder="Choose a password"
                            value={password}
                            onBlur={onBlur}
                            onChange={onPasswordChange}
                        />
                        {usingPersistedPassword && (
                            <button className="link" onClick={clearExistingPassword}>
                                Change
                            </button>
                        )}
                    </StyledPopupInputContainer>
                </div>
            </ToggleContents>
        </div>
    );
};

PublishBoardPasswordToggle.propTypes = {
    isPublished: PropTypes.bool,
    canModifySettings: PropTypes.bool,
    passwordEnabled: PropTypes.bool,
    setPasswordEnabled: PropTypes.func,
    isPublishedAncestorPasswordProtected: PropTypes.bool,

    showSharedAncestorBoardLink: PropTypes.bool,
    publishedAncestorBoard: PropTypes.object,

    currentBoardId: PropTypes.string,
    currentBoard: PropTypes.object,
    currentBoardPublishedPermissionId: PropTypes.string,

    dispatchUpdatePublishedBoardPassword: PropTypes.func,
    dispatchRemovePublishedBoardPassword: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(PublishBoardPasswordToggle);
